import {
  Archive,
  Asterisk,
  BarChart3,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  Filter,
  Hash,
  Layers,
  Mail,
  MapPin,
  Phone,
  PhoneMissed,
  Scale,
  Tag,
  ToggleLeft,
  Type,
  User,
  type LucideIcon,
} from "lucide-react";
import type {
  FieldOption,
  FilterCondition,
  FilterValueType,
} from "./AdvancedFilters";

/**
 * Companies-page filter taxonomy used by the chips bar.
 *
 * The chips bar is a render layer over the page's filter state
 * (tab + advancedFilters). Each `ActiveFilter` carries a `source`
 * discriminator so callbacks know which control to sync back to
 * when the user edits or removes a chip.
 *
 *  - "tab"        — derived from `tab` (All / Prospects / Clients
 *                   / Archived). Removing this chip switches the
 *                   tab to "all".
 *  - "advanced"   — every row in advancedFilters. Has a real
 *                   UUID id; edits target that row by id. Status
 *                   and score conditions used to live in their
 *                   own "status" / "score" branches when pills +
 *                   slider were the canonical entry points; that
 *                   redundancy was removed and they now flow
 *                   through the advanced branch like any other
 *                   condition.
 */
export type FilterChipSource = "tab" | "advanced";

export interface ActiveFilter {
  /** Stable React key. Synthetic for the tab chip; the real
   *  `FilterCondition.id` for advanced rows. */
  id: string;
  source: FilterChipSource;
  /** The user-visible label for the filter's field (e.g. "Status",
   *  "Score"). For tab chips, this is the "Is client" / "Is
   *  archived" header. */
  fieldLabel: string;
  /** Resolved filter operator label (e.g. "is", "between", "is
   *  one of"). For tab chips this is fixed ("is"). */
  operatorLabel: string;
  /** The right-hand side of the chip — "false", "60 — 100", "Contact
   *  Called, To Recontact", etc. */
  valueLabel: string;
  /** The underlying field key the chip represents — used by the
   *  editor popover to look up the field's type / operators. */
  fieldKey: string;
  /** Raw operator + value (when applicable). Editor popovers seed
   *  the form from these. Tab chips set operator="eq" + value of
   *  the underlying flag (true/false). */
  operator: string;
  value: unknown;
}

/** The 4 tab values. "all" is the new default for the pristine
 *  "All" reset — it sends NO implicit is_client / archived_only,
 *  so the result set is "every non-archived company across both
 *  client states". */
export type TabKey = "all" | "prospects" | "clients" | "archived";

export interface TabSpec {
  /** Visual label rendered inside the TabsTrigger. */
  label: string;
  /** Chip text rendered on the chips bar when this tab is active.
   *  Null means "no chip" — used for the All tab, whose absence of
   *  scope is its own signal (no chip needed). */
  chip: { fieldLabel: string; operatorLabel: string; valueLabel: string } | null;
}

export const TAB_SPECS: Record<TabKey, TabSpec> = {
  all: {
    label: "All",
    chip: null,
  },
  prospects: {
    label: "Prospects",
    chip: {
      fieldLabel: "Is client",
      operatorLabel: "is",
      valueLabel: "false",
    },
  },
  clients: {
    label: "Clients",
    chip: {
      fieldLabel: "Is client",
      operatorLabel: "is",
      valueLabel: "true",
    },
  },
  archived: {
    label: "Archived",
    chip: {
      fieldLabel: "Is archived",
      operatorLabel: "is",
      valueLabel: "true",
    },
  },
};

/** Order of the tab triggers in the TabsList — left to right. */
export const TAB_ORDER: TabKey[] = ["all", "prospects", "clients", "archived"];

/** Synthetic stable key for the tab chip. Real advanced-filter
 *  chips use their FilterCondition.id (UUID), so the namespaces
 *  never collide. (The former STATUS_CHIP_ID / SCORE_CHIP_ID were
 *  retired when the status pills + score slider were removed —
 *  those conditions now render via the advanced branch using
 *  their FilterCondition.id.) */
export const TAB_CHIP_ID = "chip:tab";

/** Backend column labels are sometimes French while the UI is
 *  English. The frontend can't fix labels server-side from here,
 *  but it CAN translate known French labels at render time. Applied
 *  inside `applyLabelOverrides()` after `buildFieldOptions` — so
 *  saved views that target the underlying field keys round-trip
 *  unchanged. Add new entries here, keyed by field key (not label),
 *  whenever the backend ships a French label we want to render in
 *  English. */
export const LABEL_OVERRIDES: Record<string, string> = {
  forme_juridique: "Legal form",
  code_postal: "Postal code",
  ville: "City",
};

/** Fields the +Add filter picker MUST NOT expose — they're scope,
 *  not filters, and live on the tab row above. Note: existing saved
 *  views that carry these still render as chips (their data is
 *  preserved), but the picker won't offer to create new ones. */
export const PICKER_HIDDEN_FIELDS: ReadonlySet<string> = new Set([
  "is_client",
  "is_archived",
]);

/** Apply LABEL_OVERRIDES + filter out picker-hidden fields. The
 *  resulting list is what the +Add filter popover renders. The
 *  full unfiltered list (with original labels) is still used to
 *  resolve labels for chips coming from saved views that may
 *  carry hidden fields. */
export function applyLabelOverrides(fields: FieldOption[]): FieldOption[] {
  return fields.map((f) =>
    LABEL_OVERRIDES[f.value] ? { ...f, label: LABEL_OVERRIDES[f.value] } : f,
  );
}

export function pickerFields(fields: FieldOption[]): FieldOption[] {
  return applyLabelOverrides(fields).filter(
    (f) => !PICKER_HIDDEN_FIELDS.has(f.value),
  );
}

/** Find the operator's human label given a value-type + an operator
 *  key. Falls back to the operator key itself when the table doesn't
 *  cover it — keeps stale saved views renderable rather than
 *  rendering an empty chip. */
export function operatorLabel(
  operatorsByType: Record<string, Array<{ value: string; label: string }>>,
  type: string,
  op: string,
): string {
  const table = operatorsByType[type] ?? operatorsByType.text ?? [];
  return table.find((o) => o.value === op)?.label ?? op;
}

/** Render the value portion of a chip into a short, human label.
 *  Defers the heavy formatting to a callback so this module stays
 *  decoupled from members / pipelineStatuses lookups. */
export interface ValueLabelContext {
  /** Field → key→label map (e.g. pipeline_status → key→label). */
  catalogs: Record<string, Record<string, string>>;
  /** Field → user UUID → display name (owner_id, etc.). */
  members: Record<string, string>;
  /** Optional fallback when the value is null/undefined/empty array. */
  placeholder?: string;
}

export function valueLabelFor(
  condition: Pick<FilterCondition, "field" | "operator" | "value">,
  ctx: ValueLabelContext,
): string {
  const { field, operator, value } = condition;

  if (operator === "is_empty") return "is empty";
  if (operator === "is_not_empty") return "is not empty";

  if (operator === "between" && Array.isArray(value)) {
    const [a, b] = value;
    return `${stringify(a)} to ${stringify(b)}`;
  }

  if (Array.isArray(value)) {
    const labels = (value as unknown[])
      .map((v) => resolveOne(field, v, ctx))
      .filter((s) => s !== "");
    if (labels.length === 0) return ctx.placeholder ?? "—";
    if (labels.length === 1) return labels[0];
    if (labels.length <= 3) return labels.join(", ");
    return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  }

  const resolved = resolveOne(field, value, ctx);
  return resolved === "" ? ctx.placeholder ?? "—" : resolved;
}

function resolveOne(field: string, v: unknown, ctx: ValueLabelContext): string {
  if (v === null || v === undefined || v === "") return "";
  const cat = ctx.catalogs[field];
  if (cat && typeof v === "string" && cat[v]) return cat[v];
  if (field === "owner_id" && typeof v === "string" && ctx.members[v]) {
    return ctx.members[v];
  }
  return stringify(v);
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

// ─── Chip icon resolution (F2) ───────────────────────────────────────────
// Frontend-only mapping. FieldOption in AdvancedFilters.tsx doesn't carry
// an icon, and we don't want to widen that shape from a presentation
// concern — so the resolver decides at render time. Order of precedence:
//  1. Source-specific icons for synthetic chips (tab / status / score).
//  2. Field-key overrides for known columns (sector / owner / etc.).
//  3. FilterValueType fallback (text / number / date / …).
//  4. Final fallback: Type.

const FIELD_ICONS: Record<string, LucideIcon> = {
  name: Building2,
  score: BarChart3,
  pipeline_status: Tag,
  tags: Tag,
  owner_id: User,
  is_client: Briefcase,
  is_archived: Archive,
  created_at: Calendar,
  updated_at: CalendarClock,
  contact_phone: Phone,
  no_answer_count: PhoneMissed,
  contact_email: Mail,
  sector: Layers,
  city: MapPin,
  ville: MapPin,
  code_postal: MapPin,
  forme_juridique: Scale,
};

const TYPE_FALLBACK_ICONS: Record<FilterValueType, LucideIcon> = {
  text: Type,
  number: Hash,
  select: Tag,
  boolean: ToggleLeft,
  date: Calendar,
  tags: Tag,
  user: User,
  existence: Asterisk,
};

/** Plain-text rendering of a single chip — used by the
 *  SaveAsNewViewModal's preview list so the text shown in the
 *  modal matches the chip exactly. Pure: no hooks, no lookups.
 *  Mirrors the three styled spans of FilterChip joined by single
 *  spaces. */
export function getFilterDisplayString(filter: ActiveFilter): string {
  return `${filter.fieldLabel} ${filter.operatorLabel} ${filter.valueLabel}`.trim();
}

// ─── Multi-sort (F3-bis-frontend) ────────────────────────────────────────
// Frontend-side mirror of the backend's ALLOWED_SORTABLE_FIELDS (12 keys
// shipped in prism-backend 2fcbf23). Keep this in sync with the
// backend allow-list; sort fields outside this set will 422 server-side.
// Field key → display label, used by the SortChip / SortEditorPopover /
// CompanyTable header indicator.

export type SortDirection = "asc" | "desc";

export interface SortSpec {
  field: string;
  dir: SortDirection;
}

/** The 19 sortable field keys from the backend allow-list (12 core
 *  + 7 added in prism-backend 1d018ea). Order in this map drives the
 *  field dropdown order inside the editor — so put the most-
 *  frequently-sorted fields first. */
export const SORTABLE_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "score", label: "Score" },
  { key: "score_override", label: "Score (override)" },
  { key: "name", label: "Company" },
  { key: "domain", label: "Domain" },
  { key: "sector", label: "Sector" },
  { key: "country", label: "Country" },
  { key: "city", label: "City" },
  { key: "pipeline_status", label: "Status" },
  { key: "headcount", label: "Headcount" },
  { key: "created_at", label: "Created" },
  { key: "updated_at", label: "Updated" },
  { key: "pipeline_status_changed_at", label: "Status changed" },
  // Added with prism-backend 1d018ea. Less frequently sorted on, so
  // pushed to the bottom of the editor's field dropdown.
  { key: "is_client", label: "Is client" },
  { key: "state", label: "State" },
  { key: "postal_code", label: "Postal code" },
  { key: "tax_id", label: "Tax ID" },
  { key: "legal_form", label: "Legal form" },
  { key: "main_phone", label: "Phone" },
  { key: "archived_at", label: "Archived" },
];

const SORTABLE_FIELD_SET: ReadonlySet<string> = new Set(
  SORTABLE_FIELDS.map((s) => s.key),
);

/** Column-id → backend-sort-field-key aliases. Only listed here when
 *  the table column id diverges from the backend sort key — identity
 *  mappings are handled by `resolveSortFieldForColumn`'s `?? columnId`
 *  fallback, so e.g. `sector` / `score` / `domain` / `headcount` need
 *  no entry. Add new divergences here, NOT in the field allow-list.
 *
 *  Static-column divergences in CompanyTable.tsx:
 *  - `company` renders Company.name (id chosen for header label, not
 *    for the underlying field).
 *  - `status` renders Company.pipeline_status (short id for the chip
 *    column).
 *  - `updated` renders Company.updated_at (short id, no `_at` suffix).
 *
 *  Short-form date aliases (`created`, `pipeline_status_changed`) are
 *  included so any future column that adopts the short id sorts
 *  out-of-the-box; they're no-ops while no such column exists.
 *
 *  Legacy `effective_score` alias is kept for any caller that resolves
 *  by accessorKey rather than column id. */
export const SORT_COLUMN_ID_ALIAS: Readonly<Record<string, string>> = {
  effective_score: "score",
  company: "name",
  status: "pipeline_status",
  updated: "updated_at",
  created: "created_at",
  pipeline_status_changed: "pipeline_status_changed_at",
  // Legacy CFO column ids that still surface in available_columns
  // catalogs and saved views. LABEL_OVERRIDES already humanises them
  // for chips; route them to the canonical backend sort keys here.
  code_postal: "postal_code",
  forme_juridique: "legal_form",
  // COMPANY_COLUMN_ALIASES exposes an `archived` column id that maps
  // to the underlying `archived_at` timestamp.
  archived: "archived_at",
};

/** Resolve a table-column id to the backend sort field. Returns null
 *  when the column isn't sortable (its key isn't in the allow-list
 *  after alias resolution). */
export function resolveSortFieldForColumn(columnId: string): string | null {
  const resolved = SORT_COLUMN_ID_ALIAS[columnId] ?? columnId;
  return SORTABLE_FIELD_SET.has(resolved) ? resolved : null;
}

/** Look up a field key's display label. Falls back to the raw key
 *  when the field isn't in the allow-list — happens when a saved
 *  view references a column that's since been removed from the
 *  backend list. The editor surfaces these as readonly. */
export function sortFieldLabel(field: string): string {
  return SORTABLE_FIELDS.find((s) => s.key === field)?.label ?? field;
}

export function isSortableField(field: string): boolean {
  return SORTABLE_FIELD_SET.has(field);
}

/** Plain-text rendering of the sort state, used by SortChip's body
 *  AND the SaveAsNewViewModal's "Sort: …" preview line. Empty array
 *  returns "" so callers can short-circuit on truthy. */
export function getSortDisplayString(sorts: ReadonlyArray<SortSpec>): string {
  if (sorts.length === 0) return "";
  return sorts
    .map((s) => `${sortFieldLabel(s.field)} (${s.dir})`)
    .join(", ");
}

/** Read sorts out of a saved view's filters_json blob. Tolerant of
 *  unknown shapes — a malformed entry is skipped, not a crash. */
export function deserializeSorts(raw: unknown): SortSpec[] {
  if (!Array.isArray(raw)) return [];
  const out: SortSpec[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (typeof o.field !== "string") continue;
    const dir = o.dir === "asc" || o.dir === "desc" ? o.dir : "asc";
    out.push({ field: o.field, dir });
  }
  return out;
}

/** Pick the Lucide icon for a chip. `field` may be null when the
 *  chip references an unknown column (stale saved view); in that
 *  case we fall through to the source / field-key dictionary and
 *  finally to a generic icon. */
export function resolveChipIcon(
  filter: ActiveFilter,
  field: FieldOption | null,
): LucideIcon {
  if (filter.source === "tab") return Filter;
  // advanced — pipeline_status and score conditions now flow
  // through this branch too (their dedicated "status" / "score"
  // sources were retired alongside the pills + slider). The
  // field-key dictionary picks up `pipeline_status → Tag` and
  // `score → BarChart3` so the icons are identical to before.
  const byField = FIELD_ICONS[filter.fieldKey];
  if (byField) return byField;
  if (field) {
    const byType = TYPE_FALLBACK_ICONS[field.type];
    if (byType) return byType;
  }
  return Type;
}
